'use client';

import React, { ReactElement } from 'react';
import { Spinner } from 'react-bootstrap';
import { BiRightArrowAlt } from 'react-icons/bi';

export type LouiseButtonColorType = 'blue' | 'white' | 'gray' | 'gray-light' | 'red' | 'green' | 'purple' | 'indigo' | 'transparent' | 'transparent-text-light';
export type CtaTargetType =
  | 'register'
  | 'home'
  | 'login'
  | 'contact'
  | 'plans'
  | 'partner-register'
  | 'roi-calculator'
  | 'discover-human-clone-link'
  | 'discover-chatbot-type'
  | 'revenue-calculator'
  | 'discover-chatbot-text'
  | 'discover-chatbot-ai'
  | 'discover-videobot'
  | 'other';

type props = {
  color: LouiseButtonColorType;
  cta: string;
  ctaTarget: CtaTargetType;
  gradiant?: boolean;
  icon?: ReactElement;
  animation?: boolean;
  onClick?: () => void;
  fullWith?: boolean;
  isLoading?: boolean;
};

export default function LouiseButton({ color, cta, gradiant, icon, animation, onClick, fullWith, isLoading = false }: props) {
  let textColor, gradiantColor, bgColor;

  switch (color) {
    case 'blue':
      textColor = 'tw-text-white';
      gradiantColor = 'tw-from-blue-600 tw-to-purple-600';
      bgColor = `tw-bg-blue-600`;
      break;
    case 'white':
      textColor = 'tw-text-blue-600';
      gradiantColor = 'tw-from-gray-300 tw-to-white';
      bgColor = `tw-bg-white`;
      break;
    case 'gray':
      textColor = 'tw-text-white tw-border-1 tw-border-gray-600';
      gradiantColor = 'tw-from-gray-600 tw-to-transparent';
      bgColor = `tw-bg-gray-600`;
      break;
    case 'gray-light':
      textColor = 'tw-text-gray-900 tw-border-[1px] tw-border-gray-300';
      gradiantColor = 'tw-from-gray-500 tw-to-transparent';
      bgColor = `tw-bg-gray-100`;
      break;
    case 'green':
      textColor = 'tw-text-white';
      gradiantColor = 'tw-from-green-500 tw-to-emerald-600';
      bgColor = `tw-bg-green-600`;
      break;
    case 'purple':
      textColor = 'tw-text-white';
      gradiantColor = 'tw-from-purple-500 tw-to-pink-600';
      bgColor = `tw-bg-purple-600`;
      break;
    case 'red':
      textColor = 'tw-text-white';
      gradiantColor = 'tw-from-red-400 tw-to-red-700';
      bgColor = `tw-bg-red-600`;
      break;
    case 'indigo':
      textColor = 'tw-text-white';
      gradiantColor = 'tw-from-indigo-400 tw-to-indigo-700';
      bgColor = `tw-bg-indigo-600`;
      break;
    case 'transparent':
      textColor = 'tw-text-blue-600';
      gradiantColor = '';
      bgColor = ``;
      break;
    case 'transparent-text-light':
      textColor = 'tw-text-white/80';
      gradiantColor = '';
      bgColor = ``;
      break;
    default:
      textColor = 'tw-text-white';
      gradiantColor = 'tw-from-blue-600 tw-to-purple-600';
      bgColor = `tw-bg-${color}-600`;
      break;
  }

  const handleClick = () => {
    if (onClick) {
      onClick();
      return;
    }
  };

  const animationCss = animation ? 'tw-transition-all tw-duration-300 hover:tw-scale-105' : '';
  const outline = `tw-border-none ${textColor} tw-rounded-xl tw-font-semibold tw-font-['Lexend'] tw-px-6 tw-py-3 tw-items-center tw-justify-center tw-inline-flex tw-gap-2 ${animationCss} `;
  let commonStyle = color === 'transparent' ? outline : outline + `tw-shadow-lg hover:tw-shadow-xl`;
  if (fullWith) {
    commonStyle = commonStyle + ' tw-w-full';
  }
  const style = gradiant ? `${commonStyle} tw-bg-gradient-to-br ${gradiantColor}` : `${commonStyle} ${bgColor}`;
  return (
    <div>
      <button id="btn-louise-button" className={style} onClick={handleClick}>
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            {icon}
            <div className="tw-px-2">{cta}</div>
            <BiRightArrowAlt className="tw-w-4 tw-h-4" />
          </>
        )}
      </button>
    </div>
  );
}
